// Individual notification item component

import { useNavigate } from 'react-router'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import type { Notification } from '@/types/notification'
import {
  ThumbsUp,
  MessageCircle,
  AtSign,
  UserPlus,
  Repeat2,
  Coins,
  ThumbsDown,
  UserMinus,
  Bell,
} from 'lucide-react'

interface NotificationItemProps {
  notification: Notification
  onClose: () => void
}

function getNotificationIcon(type: Notification['type']) {
  const iconClass = 'h-4 w-4'

  switch (type) {
    case 'vote':
      return <ThumbsUp className={`${iconClass} text-green-500`} />
    case 'reply':
    case 'reply_comment':
      return <MessageCircle className={`${iconClass} text-blue-500`} />
    case 'mention':
      return <AtSign className={`${iconClass} text-purple-500`} />
    case 'follow':
      return <UserPlus className={`${iconClass} text-cyan-500`} />
    case 'reblog':
      return <Repeat2 className={`${iconClass} text-orange-500`} />
    case 'transfer':
      return <Coins className={`${iconClass} text-yellow-500`} />
    case 'unvote':
      return <ThumbsDown className={`${iconClass} text-red-500`} />
    case 'unfollow':
      return <UserMinus className={`${iconClass} text-gray-500`} />
    default:
      return <Bell className={iconClass} />
  }
}

function extractUsername(msg: string): string | null {
  const match = msg.match(/@([a-zA-Z0-9.-]+)/)
  return match?.[1] ?? null
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'just now'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours}h ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `${diffInDays}d ago`
  }

  return date.toLocaleDateString()
}

export function NotificationItem({ notification, onClose }: NotificationItemProps) {
  const navigate = useNavigate()
  const username = extractUsername(notification.msg)

  const handleClick = () => {
    onClose()

    if (notification.url) {
      // URL format: @author/permlink
      navigate(`/${notification.url}`)
    } else if (username && (notification.type === 'follow' || notification.type === 'unfollow')) {
      navigate(`/@${username}`)
    }
  }

  return (
    <button
      onClick={handleClick}
      className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
    >
      {/* User Avatar */}
      <Avatar className="h-8 w-8 shrink-0">
        {username && (
          <AvatarImage
            src={`https://steemitimages.com/u/${username}/avatar`}
            alt={username}
          />
        )}
        <AvatarFallback className="text-xs">
          {username?.[0]?.toUpperCase() || '?'}
        </AvatarFallback>
      </Avatar>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {getNotificationIcon(notification.type)}
          <span className="text-sm text-muted-foreground">
            {formatTimeAgo(notification.date)}
          </span>
        </div>
        <p className="mt-1 text-sm line-clamp-2">{notification.msg}</p>
      </div>
    </button>
  )
}
